import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Wrench, Zap, Utensils, Stethoscope, Car, Scissors, Building,
  Flame, Hammer, Key, Flower, Truck, Sparkles, Smile, Pill,
  Scale, FileText, Calculator, Camera, Ruler, HeartPulse, Beef, Search
} from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { categoriesApi } from "@/lib/api";

const categoryIcons = {
  "plomberie": Wrench,
  "electricite": Zap,
  "chauffage": Flame,
  "menuiserie": Hammer,
  "serrurerie": Key,
  "jardinage-paysagiste": Flower,
  "demenagement": Truck,
  "nettoyage": Sparkles,
  "restaurant": Utensils,
  "medecin": Stethoscope,
  "dentiste": Smile,
  "pharmacie": Pill,
  "avocat": Scale,
  "notaire": FileText,
  "comptable": Calculator,
  "garage-automobile": Car,
  "coiffeur": Scissors,
  "agence-immobiliere": Building,
  "photographe": Camera,
  "architecte": Ruler,
  "veterinaire": HeartPulse,
  "boucherie-charcuterie": Beef,
};

// Optimize image URLs for faster loading
const optimizeImageUrl = (url, width = 400) => {
  if (!url) return url;
  
  // Pexels: add auto=compress and w parameter
  if (url.includes('pexels.com')) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}auto=compress&cs=tinysrgb&w=${width}&dpr=1`;
  }
  
  // Unsplash: add w and q parameters
  if (url.includes('unsplash.com')) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}w=${width}&q=75&fm=webp`;
  }
  
  return url;
};

export default function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await categoriesApi.getAll();
        setCategories(res.data);
        setFilteredCategories(res.data);
      } catch (error) {
        console.error("Error loading categories:", error);
      } finally {
        setLoading(false);
      }
    };
    loadCategories();
  }, []);

  useEffect(() => {
    if (searchQuery) {
      const filtered = categories.filter(cat =>
        cat.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredCategories(filtered);
    } else {
      setFilteredCategories(categories);
    }
  }, [searchQuery, categories]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="categories-page">
      <Header />

      {/* Hero */}
      <div className="bg-slate-900 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Toutes les <span className="text-[#FFD700]">catégories</span>
          </h1>
          <p className="text-slate-400 max-w-2xl">
            Parcourez toutes les catégories de professionnels disponibles dans notre annuaire.
          </p>

          {/* Search */}
          <div className="mt-8 max-w-md">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Rechercher une catégorie..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                data-testid="category-search-input"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Categories Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {[...Array(15)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-slate-200">
                <div className="skeleton w-12 h-12 rounded-lg mb-4"></div>
                <div className="skeleton h-5 w-3/4"></div>
              </div>
            ))}
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="text-center py-12">
            <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Aucune catégorie trouvée
            </h3>
            <p className="text-slate-600">
              Essayez un autre terme de recherche.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {filteredCategories.map((category) => {
              return (
                <Link
                  key={category.id}
                  to={`/recherche?category_id=${category.id}`}
                  className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-[#FFD700] hover:shadow-lg transition-all"
                  data-testid={`category-card-${category.slug}`}
                >
                  {/* Image vignette */}
                  <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                    {category.image_url ? (
                      <img
                        src={optimizeImageUrl(category.image_url, 300)}
                        alt={category.name}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Building className="w-12 h-12 text-slate-300" />
                      </div>
                    )}
                  </div>
                  {/* Nom catégorie */}
                  <div className="p-3 bg-white">
                    <h3 className="font-semibold text-slate-900 text-sm text-center group-hover:text-[#2563EB]">
                      {category.name}
                    </h3>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
